import { useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button, Skeleton, Spinner } from '@design-system';
import { Eye, EyeOff, Loader2, Save, Search, Send, Wand2 } from 'lucide-react';
import { type FundingPolicyType } from '@shared';
import {
  FormEditorSidebar,
  FormEditorContent,
  useFormDefinition,
  useFormMetadata,
  useSendTestEnvelope,
} from '@/features/quote-and-apply-builder';
import {
  useFormEditorPage,
  useFormEditorLoaded,
} from '@/features/quote-and-apply-builder/hooks/use-form-editor-page';
import { generatePreviewFormData } from '../utils/preview-form-data';
import { DocusignTabsInspectorDialog } from './docusign-tabs-inspector-dialog';
import { FormPreviewContent } from './form-preview-content';
import { FormBuilderDragLayer } from './form-builder-drag-layer';

export function FormEditorPage() {
  const { carrierId, formConfig, isLoading, error, handleBack } = useFormEditorPage();

  if (isLoading) {
    return <FormEditorSkeleton />;
  }

  if (error || !formConfig) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="mb-2 text-red-600">Failed to load form.</p>
          <p className="text-sm text-gray-500">Please try again or go back.</p>
        </div>
      </div>
    );
  }

  return (
    <FormEditorLoaded
      formConfig={formConfig}
      carrierId={carrierId}
      onBack={handleBack}
    />
  );
}

export interface FormEditorLoadedProps {
  formConfig: PolicyFormConfig;
  carrierId: string;
  fundingPolicyTypeId?: FundingPolicyType[];
  stateCode?: string;
  onBack: () => void;
}

export function FormEditorLoaded({
  formConfig,
  fundingPolicyTypeId,
  stateCode,
  onBack,
}: FormEditorLoadedProps) {
  const { canSave, isSaving, handleSave } = useFormEditorLoaded({
    formConfig,
    fundingPolicyTypeId,
    stateCode,
  });
  const [isInspectorOpen, setIsInspectorOpen] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [formData, setFormData] = useState<PolicyFormSubmissionData>({});

  const formDefinition = useFormDefinition();
  const formMetadata = useFormMetadata();
  const { sendTestEnvelope, isPending: isSendingEnvelope } = useSendTestEnvelope({
    formId: formMetadata.formId,
    formDefinition,
    formData,
  });

  function handleFormDataChange(questionRef: string, value: PolicyFormSubmissionDataValue) {
    setFormData(prev => ({ ...prev, [questionRef]: value }));
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <FormBuilderDragLayer />
      <div className="flex h-screen overflow-hidden">
        <FormEditorSidebar onBack={onBack} />

        {isPreviewMode ? (
          <FormPreviewContent
            formData={formData}
            onFormDataChange={handleFormDataChange}
          />
        ) : (
          <FormEditorContent />
        )}

        <div className="fixed bottom-6 left-1/2 z-50 flex w-max -translate-x-1/2 items-center gap-2 rounded-xl border bg-white p-2">
          <Button
            variant="outline"
            onClick={() => setIsPreviewMode(prev => !prev)}
          >
            {isPreviewMode ? (
              <>
                <EyeOff />
                Exit Preview
              </>
            ) : (
              <>
                <Eye />
                Preview Form
              </>
            )}
          </Button>

          {isPreviewMode && (
            <Button
              variant="outline"
              onClick={() => setFormData(generatePreviewFormData(formDefinition))}
            >
              <Wand2 />
              Fill with Test Data
            </Button>
          )}

          {isPreviewMode && (
            <Button
              disabled={isSendingEnvelope || !formMetadata.formId}
              onClick={sendTestEnvelope}
            >
              {isSendingEnvelope ? <Spinner className="size-4" /> : <Send />}
              Send to DocuSign
            </Button>
          )}

          {!isPreviewMode && (
            <Button onClick={() => setIsInspectorOpen(true)}>
              <Search />
              Inspect DocuSign Tabs
            </Button>
          )}

          {!isPreviewMode && canSave && (
            <Button
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          )}
        </div>

        <DocusignTabsInspectorDialog
          open={isInspectorOpen}
          onOpenChange={setIsInspectorOpen}
        />
      </div>
    </DndProvider>
  );
}

function FormEditorSkeleton() {
  return (
    <div className="flex h-screen overflow-hidden">
      <div className="hidden w-80 border-r bg-white p-6 md:block">
        <div className="mb-6 flex items-center gap-4">
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-6 w-40" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton
              key={index}
              className="h-8 w-full"
            />
          ))}
        </div>
      </div>
      <div className="flex-1 bg-gray-50 p-8">
        <Skeleton className="mb-4 h-8 w-64" />
        <div className="mb-6 flex gap-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton
              key={index}
              className="h-9 w-24 rounded-md"
            />
          ))}
        </div>
        <Skeleton className="h-64 w-full rounded-md" />
      </div>
    </div>
  );
}
